import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  ReferenceField,
  DateField,
  EditButton,
  Filter,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  TextInput,
  FunctionField,
  useDataProvider,
  useListContext,
  DateInput,
  TopToolbar,
  ExportButton,
  CreateButton,
} from "react-admin";
import { Chip, Card, CardContent, Typography, Stack } from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import { orderExporter } from "./exporter/order.exporter";

const statusChoices = [
  { id: "IN_PROGRESS", name: "En cours" },
  { id: "COMPLETED", name: "Terminé" },
  { id: "CANCELLED", name: "Annulé" },
];

const statusLabels = statusChoices.reduce(
  (acc, { id, name }) => ({ ...acc, [id]: name }),
  {} as Record<string, string>
);

const statusColorMap: Record<string, "success" | "warning" | "error"> = {
  IN_PROGRESS: "warning",
  COMPLETED: "success",
  CANCELLED: "error",
};

const OrderListToolbar: React.FC = () => (
  <TopToolbar>
    <CreateButton label="Ajouter un bon de commande" />
    <ExportButton maxResults={10000000} />
  </TopToolbar>
);

const OrderListActions: React.FC = () => {
  const dataProvider = useDataProvider();
  const { filterValues } = useListContext();
  const [counts, setCounts] = React.useState<Record<string, number>>({
    IN_PROGRESS: 0,
    COMPLETED: 0,
    CANCELLED: 0,
  });

  React.useEffect(() => {
    dataProvider
      .getOrdersStats("orders", { filters: filterValues })
      .then((data: Record<string, number>) => {
        Object.keys(data).forEach((key) => {
          setCounts((prev) => ({ ...prev, [key]: data[key] }));
        });
      });
  }, [dataProvider, filterValues]);

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 2 }}>
      {statusChoices.map(({ id, name }) => (
        <Card
          key={id}
          sx={{
            width: "100%",
            textAlign: "center",
            bgcolor: (theme) => theme.palette.background.paper,
          }}
        >
          <CardContent>
            <Typography variant="subtitle2">{name}</Typography>
            <Typography variant="h6">{counts[id]}</Typography>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

const OrderFilter: React.FC = (props) => (
  <Filter {...props}>
    <ReferenceInput
      source="client"
      reference="clients"
      label="Client"
      perPage={50}
      alwaysOn
    >
      <AutocompleteInput
        optionText={(r) => `${r.internalCode} - ${r.name}`}
        fullWidth
        filterToQuery={(q) => ({
          filterTree: {
            operator: "or",
            operands: [
              { term: { name: "name", filterType: "like", value: q } },
              { term: { name: "internalCode", filterType: "like", value: q } },
            ],
          },
        })}
      />
    </ReferenceInput>
    <ReferenceInput
      source="provider"
      reference="providers"
      label="Prestataire"
      perPage={50}
      alwaysOn
    >
      <AutocompleteInput
        optionText={(r) => `${r.internalCode} – ${r.name}`}
        fullWidth
        filterToQuery={(q) => ({
          filterTree: {
            operator: "or",
            operands: [
              { term: { name: "name", filterType: "like", value: q } },
              { term: { name: "internalCode", filterType: "like", value: q } },
            ],
          },
        })}
      />
    </ReferenceInput>
    <SelectInput
      source="status"
      choices={statusChoices}
      alwaysOn
      label="Statut"
    />
    <TextInput source="orderNumber" label="Numéro BDC" alwaysOn />
    <DateInput
      source="startDate"
      label="Date de début (Prévisionnelle)"
      alwaysOn
    />
    <DateInput source="endDate" label="Date de fin (Prévisionnelle)" alwaysOn />
  </Filter>
);

export const OrderList: React.FC = (props) => (
  <List
    {...props}
    title="Bons de Commande"
    filters={<OrderFilter />}
    sort={{ field: "createdAt", order: "DESC" }}
    actions={<OrderListToolbar />}
    exporter={orderExporter}
  >
    <OrderListActions />

    <Datagrid rowClick={false} bulkActionButtons={false}>
      <ReferenceField
        source="client"
        reference="clients"
        label="Client"
        link={false}
      >
        <TextField source="internalCode" /> - <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        source="provider"
        reference="providers"
        label="Prestataire"
        link={false}
      >
        <TextField source="internalCode" /> - <TextField source="name" />
      </ReferenceField>
      <TextField
        source="quantityBin"
        label="Qte. caisse a récuperer"
        textAlign="center"
      />
      <TextField
        source="quantityAirbag"
        label="Qte. airbag en vrac"
        textAlign="center"
      />
      <TextField
        source="fullBinsCollectedCount"
        label="Qte. caisse récuprer (réel)"
        textAlign="center"
      />
      <TextField
        source="emptyBinsDepositCount"
        label="Qte. caisse déposer (réel)"
        textAlign="center"
      />
      <DateField
        textAlign="center"
        source="createdAt"
        label="Date création"
        locales="fr-FR"
        options={{
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }}
      />
      <DateField
        textAlign="center"
        source="provisionalDate"
        label="Date prévisionnelle"
        locales="fr-FR"
        options={{
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }}
      />
      <DateField
        textAlign="center"
        source="executionDate"
        label="Date d'execution"
        locales="fr-FR"
        options={{
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }}
      />
      <TextField source="orderNumber" label="Numéro BDC" textAlign="center" />
      <FunctionField
        textAlign="center"
        source="status"
        label="Statut"
        render={(record: any) => (
          <Chip
            label={statusLabels[record.status] ?? record.status}
            color={statusColorMap[record.status] || "default"}
            size="small"
          />
        )}
      />
      <FunctionField
        label="Lien QR presta"
        textAlign="center"
        render={(record) =>
          record.externalCollectLink && (
            <a
              href={record.externalCollectLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "inherit" }}
              onClick={(e: any) => {
                e.stopPropagation();
              }}
            >
              <PreviewIcon />
            </a>
          )
        }
      />
      <EditButton label="Modifier" />
    </Datagrid>
  </List>
);
